import { addMinutes } from "date-fns";
import type React from "react";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { MentionInputDto, MessageV2Dto } from "@/api/types";
import { createMentionComposerValue } from "@/components/CommunityFeed/MentionComposer";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { Form } from "@/components/Form/Form";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormMentionComposer } from "@/components/Form/FormMentionComposer";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { FormField } from "@/design-system/FormField";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { createRequiredStringRule } from "@/helpers/rules";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useTranslation } from "@/translations";

import { CommunityMessageAttachmentField } from "./CommunityMessageAttachmentField";
import { PromotedGroupCard } from "./PromotedGroupCard";

export interface CommunityMessageEditFormValues {
  title: string;
  content: string;
  contentTemplate?: string;
  mentions?: MentionInputDto[];
  images: FormImage[];
  videos: FormVideo[];
  documents: FormDocument[];
  scheduled?: Date;
}

interface CommunityMessageEditFormProps {
  message: MessageV2Dto;
  onEdit: () => void;
  onCancel: () => void;
}

/** Edits message content and attachments while preserving its structured mention fields. */
export function CommunityMessageEditForm({
  message,
  onEdit,
  onCancel,
}: CommunityMessageEditFormProps): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const form = useForm<CommunityMessageEditFormValues>({
    defaultValues: {
      title: message.title ?? "",
      ...createMentionComposerValue(message),
      images: message.images || [],
      videos: message.videos || [],
      documents: message.documents || [],
      scheduled: message.scheduledAt ? new Date(message.scheduledAt) : undefined,
    },
  });

  const currContent = useWatch({
    control: form.control,
    name: "content",
  });
  const { entity: attachedEntity } = unfurlDeepLinkFromContent(currContent);

  const { uploadFormImage } = useUploadImage();
  const { uploadFormDocument } = useUploadDocument();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});
  const updateMessageMutation = communityFeedMutations.useUpdateMessage();

  const onSubmit = async (formValues: CommunityMessageEditFormValues) => {
    setIsSubmitting(true);

    const imageUploadPromises = formValues.images.map((image) => uploadFormImage(image));
    // Curently only 1 document is allowed
    const documentUploadPromises = formValues.documents.map((document) => uploadFormDocument(document));
    // Curently only 1 video is allowed
    const videoUploadPromises = formValues.videos.map((video) => uploadFormVideo(video));

    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const documentIds = (await Promise.allSettled(documentUploadPromises))
      .map((document) => (document.status === "fulfilled" ? document.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises.map((video) => video)))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    if (
      imageIds.length === formValues.images.length &&
      documentIds.length === formValues.documents.length &&
      videoIds.length === formValues.videos.length
    ) {
      updateMessageMutation.mutate(
        {
          messageId: message.id,
          payload: {
            title: formValues.title,
            content: formValues.content,
            contentTemplate: formValues.contentTemplate,
            mentions: formValues.mentions,
            scheduledAt: formValues.scheduled ? formValues.scheduled.toISOString() : undefined,
            imageIds,
            videoIds,
            documentIds,
          },
        },
        {
          onSuccess: () => onEdit(),
          onSettled: () => setIsSubmitting(false),
        },
      );
    }
  };

  const onClickCancel = () => {
    abortUploadVideos(form.getValues("videos"));
    onCancel();
  };

  const [minScheduled] = useState(() => addMinutes(new Date(), 5));

  return (
    <Form.Root formMethods={form} onSubmit={onSubmit}>
      <Form.Body>
        <FormField label={t("page.message-feed.resident-create-post.form.title.title")} required>
          <FormInput<CommunityMessageEditFormValues>
            name="title"
            placeholder={t("page.message-feed.create.title.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.message-feed.create.title"),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.message-feed.resident-create-post.form.content.title")} required>
          <FormMentionComposer<CommunityMessageEditFormValues>
            name="content"
            data-testid="edit-post-content-input"
            contentTemplateName="contentTemplate"
            mentionsName="mentions"
            messageId={message.id}
            placeholder={t("page.message-feed.create.body.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.message-feed.create.body"),
              },
            }}
          />
        </FormField>
        {message.relatedGroup ? (
          <PromotedGroupCard
            id={message.relatedGroup.id}
            name={message.relatedGroup.name}
            description={message.relatedGroup.description}
            image={message.relatedGroup.image}
            isReadonly
            isResidentGroup={message.relatedGroup.isResidentGroup}
          />
        ) : (
          <CommunityMessageAttachmentField />
        )}
        {attachedEntity && (
          <ContentBlock width="flow" align="left">
            <EntityPreview
              entityId={attachedEntity.payload.entityId}
              entityType={attachedEntity.payload.entityType}
              otherIds={{
                folderId: attachedEntity.payload.folderId,
              }}
              readOnly
              shouldLoad
            />
          </ContentBlock>
        )}
        {message.scheduledAt && (
          <FormDatePicker<CommunityMessageEditFormValues>
            name="scheduled"
            type="datetime"
            min={minScheduled}
            rules={{
              validate: {
                laterThanMin: (date) => {
                  if (date == null) {
                    return undefined;
                  }

                  return date < minScheduled
                    ? t("page.message-feed.create.schedule.datetime.error.must-be-in-future")
                    : undefined;
                },
              },
              required: t("components.form.error.required", {
                inputName: t("page.message-feed.create.schedule.datetime"),
              }),
            }}
          />
        )}
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button variant="filled" type="submit" data-testid="edit-submit-btn" isLoading={isSubmitting}>
            {t("common.action.save")}
          </Button>
        )}
        renderSecondaryAction={() => (
          <Button variant="subtle" tone="error" onClick={onClickCancel}>
            {t("common.action.cancel")}
          </Button>
        )}
      />
    </Form.Root>
  );
}
